import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { FaqPayoff } from '../../models/faq-data.model';
import { PackageUser } from 'src/app/features/package-users/models/PackageUser.model';
import { PackageUserRole } from 'src/app/features/package-users/models/package-user-role.enum';
import { HelpFaqSelectors } from '../../store/selectors';

type FaqCategoryId = 'signing' | 'loan';

interface FaqCategory {
  id: FaqCategoryId;
  title: string;
  answers: string[];
}

@Component({
  selector: 'app-faq-window',
  templateUrl: './faq-window.component.html',
  styleUrls: ['./faq-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FaqWindowComponent {
  @Output() closed = new EventEmitter<void>();

  protected readonly categories: FaqCategory[] = [
    {
      id: 'signing',
      title: 'Signing documents',
      answers: ['copies','who', 'proof', 'nbthsign'],
    },
    {
      id: 'loan',
      title: 'Loan and financial information',
      answers: [
        'loaninfo',
        'payoffs',
        'escrow',
        'autopay',
        'discrepancies',
        'incorrect',
        'morethaniowe',
        'fundsdueatclosing',
      ],
    },
  ];

  protected expandedIndex: number | null = null;

  protected readonly isFaqLoaded$: Observable<boolean> =
    this.store.select(HelpFaqSelectors.getIsFaqLoaded);
  protected readonly cashToCloseFinalIsFromBorrower$: Observable<boolean> =
    this.store.select(HelpFaqSelectors.getCashToCloseFinalIsFromBorrower);
  protected readonly displayFundingTransfer$: Observable<boolean> =
    this.store.select(HelpFaqSelectors.getDisplayFundingTransfer);
  protected readonly disbursementDate$: Observable<string | null> =
    this.store.select(HelpFaqSelectors.getDisbursementDate);
  protected readonly firstPaymentDate$: Observable<string | null> =
    this.store.select(HelpFaqSelectors.getFirstPaymentDate);
  protected readonly interestRate$: Observable<number | null> =
    this.store.select(HelpFaqSelectors.getInterestRate);
  protected readonly monthlyPayment$: Observable<number | null> =
    this.store.select(HelpFaqSelectors.getMonthlyPayment);
  protected readonly prepaymentPenalty$: Observable<string | null> =
    this.store.select(HelpFaqSelectors.getPrepaymentPenalty);
  protected readonly payoffs$: Observable<FaqPayoff[]> =
    this.store.select(HelpFaqSelectors.getPayoffs);

  protected readonly borrowers$: Observable<PackageUser[]> = this.store.select(
    HelpFaqSelectors.getSigners
  );

  protected readonly hasNbthSigner$: Observable<boolean> = this.store.select(
    HelpFaqSelectors.getHasNbthSigner
  );

  constructor(private readonly store: Store) {}

  protected toggle(index: number): void {
    this.expandedIndex = this.expandedIndex === index ? null : index;
  }

  protected isExpanded(index: number): boolean {
    return this.expandedIndex === index;
  }

  protected onClose(): void {
    this.closed.emit();
  }

  protected trackByAnswer(_: number, answer: string): string {
    return answer;
  }

  protected trackByPayoffRecipient(_: number, payoff: FaqPayoff): string {
    return payoff.recipient;
  }

  protected trackByPackageUserGuid(_: number, user: PackageUser): string {
    return user.packageUserGuid;
  }

  protected roleLabel(role: PackageUserRole): string {
    switch (role) {
      case PackageUserRole.BORROWER:
        return 'Borrower';
      case PackageUserRole.SELLER:
        return 'Seller';
      case PackageUserRole.SIGNER:
        return 'Signer';
      case PackageUserRole.NBTH:
        return 'NBTH';
      case PackageUserRole.WITNESS:
        return 'Witness';
      case PackageUserRole.SIGNINGAGENT:
        return 'Signing Agent';
      default:
        return '';
    }
  }
}
